import { cn } from "@guru/shared/utils/cn";
import { Plus } from "lucide-react";
import type { ReactNode } from "react";

export type CellTone = "plain" | "muted" | "due" | "highlight" | "insert";

export interface SheetCell {
  text: string;
  tone?: CellTone;
  align?: "left" | "right" | "center";
}

export interface SheetRow {
  id: string;
  cells: SheetCell[];
}

function cellToneClass(tone: CellTone | undefined) {
  switch (tone) {
    case "due":
      return "bg-red-50 text-[#b91c1c] dark:bg-red-500/15 dark:text-red-400";
    case "highlight":
      return "bg-amber-100/80 font-semibold text-foreground dark:bg-amber-500/20";
    case "insert":
      return "bg-sky-50 text-foreground dark:bg-sky-500/15";
    case "muted":
      return "bg-card text-muted-foreground";
    default:
      return "bg-card text-foreground";
  }
}

/** A spreadsheet grid: header row of column letters, then data rows. */
export function SheetTable({
  columns,
  rows,
  colTemplate,
}: {
  columns: string[];
  rows: SheetRow[];
  colTemplate: string;
}) {
  return (
    <div
      className="grid gap-px bg-border text-[10px] sm:text-[11px]"
      style={{ gridTemplateColumns: colTemplate }}
    >
      {columns.map((column) => (
        <div key={column} className="bg-muted px-2 py-1 font-semibold text-muted-foreground">
          {column}
        </div>
      ))}
      {rows.map((row) =>
        row.cells.map((cell, index) => (
          <div
            key={`${row.id}-${columns[index]}`}
            className={cn(
              "px-2 py-1 tabular-nums",
              cellToneClass(cell.tone),
              cell.align === "right" && "text-right",
              cell.align === "center" && "text-center"
            )}
          >
            {cell.text}
          </div>
        ))
      )}
    </div>
  );
}

/** Excel / Google-Sheets surface: formula bar, grid, sheet-tab strip. */
export function SheetFrame({
  cellRef,
  formula,
  tabs,
  children,
}: {
  cellRef: string;
  formula: string;
  tabs: string[];
  children: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col bg-[#f6f8fc] font-mono dark:bg-[#1a1c1e]">
      {/* Formula bar */}
      <div className="flex items-center gap-2 border-b border-border bg-card px-3 py-1.5">
        <span className="rounded border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
          {cellRef}
        </span>
        <span className="text-[11px] italic text-muted-foreground/50">fx</span>
        <span className="truncate text-[11px] text-foreground">{formula}</span>
      </div>
      {/* Grid */}
      <div className="flex-1 p-3">{children}</div>
      {/* Sheet tabs */}
      <div className="flex items-center gap-1 border-t border-border bg-card px-2 py-1">
        {tabs.map((tab, index) => (
          <span
            key={tab}
            className={cn(
              "rounded-t px-2 py-0.5 text-[10px]",
              index === 0
                ? "bg-background font-semibold text-foreground shadow-sm"
                : "text-muted-foreground"
            )}
          >
            {tab}
          </span>
        ))}
        <Plus className="ml-0.5 h-3 w-3 text-muted-foreground/50" aria-hidden="true" />
      </div>
    </div>
  );
}

/** A scrawled sticky note, pinned to a corner of a spreadsheet. */
export function StickyNote({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "absolute rounded-sm bg-yellow-200 px-2 py-1 text-[10px] font-medium text-yellow-900 shadow-md dark:bg-yellow-300/90",
        className
      )}
    >
      {children}
    </div>
  );
}
